import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getLoginUrl } from "@/const";
import { ClipboardList, Droplets, FileText, Settings, Users, Upload, QrCode, Archive, Database, Sun, Moon, Shield, TrendingUp } from "lucide-react";
import { Link } from "wouter";
import { useTheme } from "@/contexts/ThemeContext";
import { CityPortalBanner } from "@/components/layout/CityPortalBanner";
import { getPostLoginRoute } from "@/lib/departmentRouter";
import { useLocation } from "wouter";
import { useEffect } from "react";

export default function Home() {
  const { user, loading, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [location, navigate] = useLocation();
  
  // Redirect non-admin users to their department module after login
  useEffect(() => {
    if (isAuthenticated && user && user.role !== 'admin') {
      const targetRoute = getPostLoginRoute(user);
      if (location === '/' && targetRoute !== '/') {
        navigate(targetRoute);
      }
    }
  }, [isAuthenticated, user, location, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background">
        <CityPortalBanner />

        {/* Login Card */}
        <div className="flex items-center justify-center px-4 py-12">
          <Card className="relative max-w-md w-full bg-card border-border shadow-lg">
            <CardHeader className="text-center space-y-4">
              <div className="mx-auto w-20 h-20 bg-secondary border-2 border-primary/30 rounded-lg flex items-center justify-center">
                <Droplets className="w-10 h-10 text-primary" />
              </div>
              <div>
                <CardTitle className="text-3xl font-bold text-foreground mb-2">City Water Well Logging</CardTitle>
                <CardDescription className="text-muted-foreground text-base">
                  Professional Water Operations Management
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3 text-center">
                <p className="text-sm text-muted-foreground">
                  Track daily logs, monitor chlorine levels, and generate comprehensive reports for municipal water well operations.
                </p>
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>System Online</span>
                </div>
              </div>
              <Button asChild className="w-full h-12 text-base font-semibold" size="lg">
                <a href={getLoginUrl()}>Sign in with Email</a>
              </Button>
              
              {/* Powered by Loveitio */}
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-center">
                  <p className="text-[10px] text-[#94A3B8]">Powered by Loveitio</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <CityPortalBanner />

      {/* User Header */}
      <header className="border-b bg-card">
        <div className="container py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Droplets className="w-6 h-6 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Water Well Logging</h1>
                <p className="text-sm text-muted-foreground">Welcome, {(user?.name || user?.email || "User")}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="h-9 w-9"
                title={theme === "dark" ? "Switch to Daylight theme" : "Switch to Industrial Dark theme"}
              >
                {theme === "dark" ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>
              <Link href="/profile">
                <Button variant="outline" size="sm">Profile</Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-2">Dashboard</h2>
          <p className="text-muted-foreground">Quick access to your daily operations</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Link href="/help">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <div className="w-12 h-12 bg-slate-500/10 rounded-lg flex items-center justify-center mb-3">
                  <FileText className="w-6 h-6 text-slate-600 dark:text-slate-300" />
                </div>
                <CardTitle>Field Guide / Help</CardTitle>
                <CardDescription>How chlorine tracking, configs, and audits work</CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link href="/logs/new">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-3">
                  <ClipboardList className="w-6 h-6 text-primary" />
                </div>
                <CardTitle>New Daily Log</CardTitle>
                <CardDescription>Create a new well log entry</CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link href="/logs">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-3">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>View Logs</CardTitle>
                <CardDescription>Browse and manage daily logs</CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link href="/reports">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-3">
                  <FileText className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Reports</CardTitle>
                <CardDescription>Generate weekly and monthly reports</CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link href="/data-viewer">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-500/10 rounded-lg flex items-center justify-center mb-3">
                  <Database className="w-6 h-6 text-cyan-600" />
                </div>
                <CardTitle>Database Viewer</CardTitle>
                <CardDescription>Browse and search all daily log records</CardDescription>
              </CardHeader>
            </Card>
          </Link>

          <Link href="/reports/all-wells-production">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
              <CardHeader>
                <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-3">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <CardTitle>All Wells Production</CardTitle>
                <CardDescription>Monthly matrix report across all wells</CardDescription>
              </CardHeader>
            </Card>
          </Link>

        </div>

        {/* Admin and Supervisor Section */}
        {(user?.role === 'admin' || user?.role === 'supervisor') && (
          <>
            <div className="mb-4">
              <h3 className="text-xl font-semibold">Administration</h3>
              <p className="text-sm text-muted-foreground">Manage system settings and users</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Link href="/wells">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-3">
                      <Settings className="w-6 h-6 text-purple-600" />
                    </div>
                    <CardTitle>Manage Wells</CardTitle>
                    <CardDescription>Configure wells and sites</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/users">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center mb-3">
                      <Users className="w-6 h-6 text-orange-600" />
                    </div>
                    <CardTitle>Manage Users</CardTitle>
                    <CardDescription>User roles and permissions</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/custom-fields">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-indigo-500/10 rounded-lg flex items-center justify-center mb-3">
                      <Settings className="w-6 h-6 text-indigo-600" />
                    </div>
                    <CardTitle>Custom Fields</CardTitle>
                    <CardDescription>Define optional form fields</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/csv-import">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center mb-3">
                      <Upload className="w-6 h-6 text-green-600" />
                    </div>
                    <CardTitle>CSV Import</CardTitle>
                    <CardDescription>Import historical well log data</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/system-health">
                <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-3">
                      <Settings className="w-6 h-6 text-purple-600" />
                    </div>
                    <CardTitle>System Health</CardTitle>
                    <CardDescription>Monitor app status and diagnostics</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/supervisor/stats">
                <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-3">
                      <TrendingUp className="w-6 h-6 text-emerald-600" />
                    </div>
                    <CardTitle>Monthly Stats</CardTitle>
                    <CardDescription>Per-well aggregates and system totals</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/data-export">
                <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-3">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <CardTitle>Data Export</CardTitle>
                    <CardDescription>Backup and export all system data</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/qr-codes">
                <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-indigo-500/10 rounded-lg flex items-center justify-center mb-3">
                      <QrCode className="w-6 h-6 text-indigo-600" />
                    </div>
                    <CardTitle>QR Codes</CardTitle>
                    <CardDescription>Generate printable well QR codes</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/archive-export">
                <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center mb-3">
                      <Archive className="w-6 h-6 text-purple-600" />
                    </div>
                    <CardTitle>Archive Export</CardTitle>
                    <CardDescription>Monthly archives with reports and photos</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              <Link href="/retention-settings">
                <Card className="hover:shadow-md transition-shadow cursor-pointer h-full">
                  <CardHeader>
                    <div className="w-12 h-12 bg-pink-500/10 rounded-lg flex items-center justify-center mb-3">
                      <Database className="w-6 h-6 text-pink-600" />
                    </div>
                    <CardTitle>Retention Settings</CardTitle>
                    <CardDescription>Configure storage and archival policies</CardDescription>
                  </CardHeader>
                </Card>
              </Link>

              {user?.role === 'admin' && (
                <Link href="/admin/health">
                  <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-950/20 dark:to-pink-950/20 border-red-200 dark:border-red-800">
                    <CardHeader>
                      <div className="w-12 h-12 bg-red-500/10 rounded-lg flex items-center justify-center mb-3">
                        <Shield className="w-6 h-6 text-red-600" />
                      </div>
                      <CardTitle>Admin Health</CardTitle>
                      <CardDescription>Advanced diagnostics and system management</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
